import { useLoader } from "@react-three/fiber";
import { TextureLoader } from "three";
import { FONT_TYPES, IComponentType, IComponentUnion, IContent360Scene, IRoot, ISceneComp, ITrackingTypes } from "../components/r3f-components/component-data-structure";
import { preloadFont } from 'troika-three-text/src';
import { preloadGltf } from "../components/r3f-components/components/Model3d/hooks";
import { preInitialiseWorkerWithArrayBuffers } from "../components/r3f-components/staticGeometryWorkerController/worker-utils";
import { Jute } from "..";
import { filestore, FsImage, FsImage360, FsModel3D } from "@/filestore";
import { getFaceTrackingComponentIdsBySceneId } from "@/components/r3f-components/utils/face-tracking";
import { isAbstractComponent } from "@/components/r3f-components/utils/general";

interface IPreloadInput {
	componentsById: { [id: string]: IComponentUnion };
	rootId: string;
}

export const useAssetPreloader = ({
	componentsById,
	rootId,
}: IPreloadInput): Promise<any> => {
	const root = componentsById[rootId] as IRoot;
	const sceneIds = root.children;
	let imgUrls: string[] = [];
    let model3dUrls: string[] = [];
    // let hlsData: IHlsPreloadData[] = [];
    let fontUrls: string[] = [];
    let promises: Promise<any>[] = [];

	for (let i = 0; i < sceneIds.length; i++) {
		const sceneId = sceneIds[i];
        const scene = componentsById[sceneId] as ISceneComp;

        let entityIds: string[];
        if (scene.trackingType === ITrackingTypes.face) {
            entityIds = getFaceTrackingComponentIdsBySceneId(sceneId, componentsById)
        } else {
            entityIds = scene.children.filter(componentId => !isAbstractComponent(componentsById[componentId]));
        }

		if (!entityIds || entityIds.length === 0) continue;
		for (let j = 0; j < entityIds.length; j++) {
			const component = componentsById[entityIds[j]];
            switch (component.type) {
                case IComponentType.Scene: {
                    if ((component as IContent360Scene).content360) {
                        const {filestoreId, type} = (component as IContent360Scene).content360 ?? {};
                        if (type === 'image') {
                            const imgFile = filestore.get<FsImage360>(filestoreId)
                            if (!imgUrls.includes(imgFile.getImageUrl())) imgUrls.push(imgFile.getImageUrl());
                        }
                    }
                    break;
                }
                case IComponentType.Model3d: {
                    const url = filestore.get<FsModel3D>(component.filestoreId).getModelUrl()
                    if (!model3dUrls.includes(url)) model3dUrls.push(url);
                    break;
                }
                case IComponentType.Image: {
                    const url = filestore.get<FsImage>(component.filestoreId).getImageUrl();
                    if (!imgUrls.includes(url)) imgUrls.push(url);
                    break;
                }
                // TODO: fix video preload as id always undefined
                // case IComponentType.Video: {
                //     const {videoUrl: url, id, muted, loop} = component;
                //     if (!hlsData.filter(data => data.url ===url).length) {
                //         hlsData.push({url, id: entityIds[j], muted, loop})
                //     }
                //     break;
                // }
                case IComponentType.Text:
                case IComponentType.Text3d:
                case IComponentType.Button: {
                    const { url } =
                        FONT_TYPES.filter((font) => font.fontFamily === component.fontFamily)[0] ||
                        FONT_TYPES[0];

                    if (!fontUrls.includes(url)) {
                        // console.log(`add font promise ${component.fontFamily}`)
                        promises.push(
                            new Promise(res => {
                                preloadFont(
                                        {
                                            font: url,
                                            characters: component?.text?.split(''),
                                            sdfGlyphSize: component.fontSize,
                                        },
                                        () => {
                                            res(true)
                                        }
                                    );
                            })
                        )   
                    }
                    fontUrls.push(url)
                    break;
                }
                default:
                    break;
            }
		}
	}

    let imgTexturePromise = new Promise(res => {
        useLoader.preload(TextureLoader, imgUrls as any, (loader) => {
            loader.manager.onLoad = () => {
                res(true)
            }
        });
    });

    const model3dPromise = new Promise((res) => {
        preloadGltf(model3dUrls).then((data) => {
            if (data === null) res(true);
            preInitialiseWorkerWithArrayBuffers(Jute.staticGeometryWorkerController, data!).then(() => {
                res(true);
            })
        })
    })

    // const model3dPromise = preloadGltf(model3dUrls)

    if (!promises.length) promises = [new Promise(res => res(true))]
    if (!imgUrls.length) imgTexturePromise = new Promise(res => res(true));

    // let vidTexturePromise = useHlsPreloader(hlsData);
    // if (!hlsData.length) vidTexturePromise = new Promise(res => res(true));

    // let vidTexturePromise = new Promise(res => res(true));

    // collect and return as one promise
    // return Promise.all([...promises, imgTexturePromise, vidTexturePromise, model3dPromise])
    //     .then(() => console.log('useAssetPreloader: asset preloading complete'));

    return Promise.all([...promises, imgTexturePromise, model3dPromise])
        .then(() => console.log('useAssetPreloader: asset preloading complete'));
};
